import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ensureHttps } from '@/utils/url';
import { VideoFolderPicker } from '@/components/shared/VideoFolderPicker';
import { useAuth } from '@/hooks/useAuth';
import type { AddPixelData, PixelPlatform } from '@/types/pixel';

interface AddPixelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: AddPixelData) => void | Promise<void>;
  flow?: { id: string; name: string };
  videos: Array<{ id_video: string; nomeVideo: string }>;
  isLoadingVideos?: boolean;
  /** Plataforma de origem (card clicado em Configurações). Default facebook (legado). */
  initialPlatform?: PixelPlatform;
}

const PLATFORM_CONFIG: Record<
  PixelPlatform,
  { badge: string; idLabel: string; idPlaceholder: string; tokenLabel: string; tokenPlaceholder: string }
> = {
  facebook: {
    badge: 'Facebook',
    get idLabel() { return translateInterfaceText("ID do pixel"); },
    get idPlaceholder() { return translateInterfaceText("Cole o ID do pixel (ex: 123456789)"); },
    tokenLabel: 'Token',
    get tokenPlaceholder() { return translateInterfaceText("Cole o token do pixel"); },
  },
  'google-ads': {
    badge: 'Google Ads',
    get idLabel() { return translateInterfaceText("ID de conversão"); },
    idPlaceholder: 'AW-123456789',
    get tokenLabel() { return translateInterfaceText("Rótulo de conversão (Label)"); },
    get tokenPlaceholder() { return translateInterfaceText("Cole o rótulo (ex: AbC-D_efG-h12)"); },
  },
  tiktok: {
    badge: 'TikTok',
    idLabel: 'ID do pixel',
    get idPlaceholder() { return translateInterfaceText("Cole o ID do pixel TikTok"); },
    get tokenLabel() { return translateInterfaceText("Token de acesso"); },
    get tokenPlaceholder() { return translateInterfaceText("Cole o token do TikTok"); },
  },
};

// Opção 1 (Google Ads): um Label por ação de conversão no painel do Google.
const GOOGLE_LABEL_FIELDS = [
  { key: 'play', get label() { return translateInterfaceText("Rótulo da ação \"Play\" *"); }, get hint() { return translateInterfaceText("Dispara no primeiro play do vídeo"); }, get placeholder() { return translateInterfaceText("Rótulo (Label) da conversão de play"); } },
  { key: 'checkout', get label() { return translateInterfaceText("Rótulo da ação \"Checkout\" (opcional)"); }, get hint() { return translateInterfaceText("Dispara no clique do botão de compra"); }, get placeholder() { return translateInterfaceText("Rótulo (Label) da conversão de checkout"); } },
  { key: 'pitch', get label() { return translateInterfaceText("Rótulo da ação \"Pitch\" (opcional)"); }, get hint() { return translateInterfaceText("Dispara quando a oferta aparece"); }, get placeholder() { return translateInterfaceText("Rótulo (Label) da conversão do pitch"); } },
] as const;

function normalizeGoogleId(value: string): string {
  let s = value.trim().toUpperCase().replace(/\s+/g, '');
  if (/^\d+$/.test(s)) s = `AW-${s}`;
  return s;
}

export function AddPixelModal({ open, onOpenChange, onSave, videos, isLoadingVideos = false, initialPlatform = 'facebook', flow }: AddPixelModalProps) {
  useLanguage();
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const [formData, setFormData] = useState({
    platform: initialPlatform,
    token: '',
    pixelId: '',
    siteUrl: '',
    videoId: '',
    googleLabels: { play: '', checkout: '', pitch: '' },
  });
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('pixel', 'edit');

  useEffect(() => {
    if (open) {
      // Sincroniza plataforma a cada abertura (card clicado)
      setFormData((prev) => ({ ...prev, platform: initialPlatform }));
      if (!canEdit) {
        toast.error(translateInterfaceText("Sem permissão para adicionar pixels"));
        onOpenChange(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPlatform]);

  const cfg = PLATFORM_CONFIG[formData.platform] ?? PLATFORM_CONFIG.facebook;

  const resetForm = () =>
    setFormData({
      platform: initialPlatform,
      token: '',
      pixelId: '',
      siteUrl: '',
      videoId: '',
      googleLabels: { play: '', checkout: '', pitch: '' },
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current || !canEdit) return;

    // Validar se o vídeo foi selecionado
    if (!flow && !formData.videoId) {
      toast.error(translateInterfaceText("Por favor, selecione um vídeo"));
      return;
    }

    if (!formData.pixelId.trim()) {
      toast.error(translateInterfaceText("Por favor, informe o {value0}", { value0: (cfg.idLabel) }));
      return;
    }

    let token = '';

    if (formData.platform === 'google-ads') {
      // Opção 1: um Label por ação (play obrigatório; checkout/pitch opcionais).
      if (!formData.googleLabels.play.trim()) {
        toast.error(translateInterfaceText("Informe o Rótulo (Label) da ação de Play do Google Ads."));
        return;
      }
      token = JSON.stringify({
        play: formData.googleLabels.play.trim(),
        ...(formData.googleLabels.checkout.trim() ? { checkout: formData.googleLabels.checkout.trim() } : {}),
        ...(formData.googleLabels.pitch.trim() ? { pitch: formData.googleLabels.pitch.trim() } : {}),
      });
    } else {
      if (!formData.token.trim()) {
        toast.error(translateInterfaceText("Por favor, informe o {value0}", { value0: (cfg.tokenLabel) }));
        return;
      }
      token = formData.token.trim();
    }

    // Google Ads: normaliza AW- na borda ( attrs: 1080... -> AW-1080...)
    let pixelId =
      formData.platform === 'google-ads' ? normalizeGoogleId(formData.pixelId) : formData.pixelId.trim();

    if (formData.platform === 'google-ads' && !/^AW-\d+$/.test(pixelId)) {
      toast.error(translateInterfaceText("ID de conversão inválido. Use AW-123456789."));
      return;
    }

    // TikTok: pixel code alfanumérico (ex.: DAL7CD3C77U2FG646HOG) — mesma regra BE/player
    if (formData.platform === 'tiktok') {
      pixelId = pixelId.replace(/[\s-]/g, '').toUpperCase();
      if (!/^[A-Z0-9]{6,20}$/.test(pixelId)) {
        toast.error(translateInterfaceText("Pixel ID do TikTok inválido. Use o ID alfanumérico do Events Manager."));
        return;
      }
    }

    // Buscar o nome do vídeo selecionado
    const selectedVideo = videos.find(v => v.id_video === formData.videoId);
    const videoName = selectedVideo?.nomeVideo || 'Vídeo';

    // Garantir que a URL tenha https://
    const siteUrlWithHttps = ensureHttps(formData.siteUrl);

    // Sempre enviar como ativo, incluindo o nome do vídeo
    savingRef.current = true;
    setIsSaving(true);
    try {
      await onSave({ platform: formData.platform, videoId: flow ? undefined : formData.videoId, flowId: flow?.id, pixelId, token, siteUrl: siteUrlWithHttps, videoName, active: true });
      resetForm();
    } catch {
      // O hook informa o erro; preservar os campos para corrigir ou tentar novamente.
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      // Resetar formulário ao fechar
      resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent aria-describedby={undefined} className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-32px)] sm:w-[480px] sm:max-w-[480px] rounded-[18px] p-7 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold text-[#1E293B] text-left">
            {translateInterfaceText("Adicionar Pixel")}{' '}
            <span className="ml-1 inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-[12px] font-semibold text-[#437DFF]">
              {cfg.badge}
            </span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* ID (pixelId / conversion id) */}
          <div className="space-y-2">
            <Label className="text-[13px] font-semibold text-[#1E293B]">{cfg.idLabel}</Label>
            <Input
              value={formData.pixelId}
              onChange={(e) => setFormData({ ...formData, pixelId: e.target.value })}
              placeholder={cfg.idPlaceholder}
              className="h-[42px] rounded-[10px] border-[#E2E8F0]"
              required
            />
          </div>

          {/* Token / Label(s) */}
          {formData.platform === 'google-ads' ? (
            <div className="space-y-4">
              {GOOGLE_LABEL_FIELDS.map((f) => (
                <div key={f.key} className="space-y-2">
                  <Label className="text-[13px] font-semibold text-[#1E293B]">{f.label}</Label>
                  <Input
                    value={formData.googleLabels[f.key]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        googleLabels: { ...formData.googleLabels, [f.key]: e.target.value },
                      })
                    }
                    placeholder={f.placeholder}
                    className="h-[42px] rounded-[10px] border-[#E2E8F0]"
                  />
                  <p className="text-[11px] text-[#64748B]">{f.hint}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-[13px] font-semibold text-[#1E293B]">{cfg.tokenLabel}</Label>
              <Input
                value={formData.token}
                onChange={(e) => setFormData({ ...formData, token: e.target.value })}
                placeholder={cfg.tokenPlaceholder}
                className="h-[42px] rounded-[10px] border-[#E2E8F0]"
                required
              />
            </div>
          )}

          {/* URL do Site */}
          <div className="space-y-2">
            <Label className="text-[13px] font-semibold text-[#1E293B]">{translateInterfaceText("URL do site")}</Label>
            <Input
              value={formData.siteUrl}
              onChange={(e) => setFormData({ ...formData, siteUrl: e.target.value })}
              placeholder="http://www.exemplo.com"
              className="h-[42px] rounded-[10px] border-[#E2E8F0]"
              required
            />
          </div>

          {flow ? (
            <div className="space-y-2 rounded-lg border border-blue-100 bg-blue-50 p-3">
              <Label>{translateInterfaceText("Funil da VSL Dinâmica")}</Label>
              <p className="text-sm font-semibold">{flow.name}</p>
              <p className="text-xs text-slate-600">{translateInterfaceText("O pixel acompanha todos os vídeos do funil publicado, incluindo os adicionados depois.")}</p>
            </div>
          ) : (
          <div className="space-y-2">
            <Label className="text-[13px] font-semibold text-[#1E293B]">{translateInterfaceText("Selecionar Vídeo")}</Label>
            <VideoFolderPicker
              value={formData.videoId}
              videos={videos.map((v) => ({ id: v.id_video, title: v.nomeVideo }))}
              onChange={(value) => setFormData({ ...formData, videoId: value })}
              placeholder={isLoadingVideos ? translateInterfaceText("Carregando vídeos...") : translateInterfaceText("Selecione um vídeo")}
              disabled={isLoadingVideos}
              triggerClassName="flex h-[42px] w-full items-center justify-between gap-2 rounded-[10px] border border-[#E2E8F0] bg-background px-3 text-sm text-[#1E293B] hover:border-[#437DFF] transition-colors disabled:opacity-50"
            />
          </div>

          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              onClick={() => handleClose(false)}
              variant="outline"
              className="flex-1 h-[42px] rounded-[10px] border-[#CBD5E1] text-[#475569] hover:text-[#FFFFFF]"
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button
              type="submit"
              disabled={isSaving || !canEdit}
              className="flex-1 h-[42px] rounded-[10px] bg-[#437DFF] hover:bg-[#355FCC] font-semibold"
            >
              {isSaving ? translateInterfaceText("Salvando…") : translateInterfaceText("Adicionar Pixel")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
